// src/services/dashboardService.js
// URL absoluta para o backend Django
const API_BASE_URL = '/dashboard/api';

class DashboardService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Método auxiliar para fazer requisições com debug melhorado
  async makeRequest(url, options = {}) {
    const token = localStorage.getItem('access_token');
    
    const defaultOptions = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      }
    };

    // Adicionar token apenas se existir
    if (token) {
      defaultOptions.headers['Authorization'] = `Bearer ${token}`;
    }

    const requestOptions = { ...defaultOptions, ...options };
    const fullUrl = `${this.baseURL}${url}`;

    try {
      console.log(`🚀 Request: ${requestOptions.method || 'GET'} ${fullUrl}`);
      
      const response = await fetch(fullUrl, requestOptions);
      
      console.log(`📡 Response status: ${response.status} ${response.statusText}`);
      
      // Verificar se a resposta é JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.error('❌ Response não é JSON:', contentType);
        const textResponse = await response.text();
        console.error('Response body:', textResponse.substring(0, 200));
        throw new Error(`Servidor retornou ${response.status}: ${response.statusText}. Verifique se a URL está correta.`);
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP Error: ${response.status}`);
      }

      const jsonResponse = await response.json();
      console.log('✅ Response data:', jsonResponse);
      return jsonResponse;

    } catch (error) {
      console.error('❌ Request failed:', error);
      
      // Melhor mensagem de erro para o usuário
      if (error.message.includes('Failed to fetch')) {
        throw new Error('Erro de conexão. Verifique se o servidor Django está rodando na porta 8000.');
      }
      
      throw error;
    }
  }

  // Buscar resumo/estatísticas (compatível com backend)
  async getSummary(filters = {}) {
    const params = new URLSearchParams();

    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.status && filters.status.length > 0) params.append('status', Array.isArray(filters.status) ? filters.status.join(',') : filters.status);
    if (filters.equipment && filters.equipment.length > 0) params.append('equipment', Array.isArray(filters.equipment) ? filters.equipment.join(',') : filters.equipment);
    if (filters.technician) params.append('technician', filters.technician);
    if (filters.type && filters.type.length > 0) params.append('type', Array.isArray(filters.type) ? filters.type.join(',') : filters.type);
    if (filters.search) params.append('search', filters.search);

    const queryString = params.toString();
    const url = queryString ? `/summary/?${queryString}` : '/summary/';

    return await this.makeRequest(url);
  }

  // Buscar ordens de serviço (compatível com backend)
  async getOrders(filters = {}, page = 1, pageSize = 20) {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString()
    });

    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.status && filters.status.length > 0) params.append('status', Array.isArray(filters.status) ? filters.status.join(',') : filters.status);
    if (filters.search) params.append('search', filters.search);
    if (filters.equipment && filters.equipment.length > 0) params.append('equipment', Array.isArray(filters.equipment) ? filters.equipment.join(',') : filters.equipment);
    if (filters.technician) params.append('technician', filters.technician);
    if (filters.type && filters.type.length > 0) params.append('type', Array.isArray(filters.type) ? filters.type.join(',') : filters.type);

    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);

    return await this.makeRequest(`/orders/?${params.toString()}`);
  }

  // Sincronizar dados com a API M8 (com parâmetros obrigatórios)
  async syncData(syncParams) {
    if (!syncParams.start_date || !syncParams.end_date) {
      throw new Error('Parâmetros start_date e end_date são obrigatórios');
    }

    return await this.makeRequest('/sync/', {
      method: 'POST',
      body: JSON.stringify(syncParams)
    });
  }

  // Testar conexão com API M8
  async testConnection() {
    return await this.makeRequest('/test-connection/', {
      method: 'POST'
    });
  }

  // Salvar configuração de conexão API
  async saveConnection(connectionData) {
    return await this.makeRequest('/save-connection/', {
      method: 'POST',
      body: JSON.stringify(connectionData)
    });
  }

  // Buscar lista de equipamentos distintos
  async getEquipmentList(search = '') {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    const queryString = params.toString();
    const url = queryString ? `/equipment/?${queryString}` : '/equipment/';
    return await this.makeRequest(url);
  }

  // Método de debug para testar se as URLs estão funcionando
  async debugUrls() {
    const urls = [
      '/summary/',
      '/orders/',
      '/test-connection/'
    ];

    for (const url of urls) {
      try {
        const response = await fetch(`${this.baseURL}${url}`);
        console.log(`${url}: ${response.status} ${response.statusText}`);
      } catch (error) {
        console.error(`${url}: ${error.message}`);
      }
    }
  }
}

// Instância singleton
export const dashboardService = new DashboardService();

// Debug helper - remover em produção
window.dashboardService = dashboardService;
